// synced from loomweb blocks/remote-file-transfer-block-view.tsx (de-Tailwind'd).
/**
 * Remote file-transfer block (RemoteFileTransfer). Faithful to loomweb's
 * RemoteFileTransferBlockView in intent: a header (icon + "Remote File Transfer"
 * with an optional `@ <env>` remote-host label, a copy button), a from/to path
 * body, and a collapsible result panel. loomweb's browser-only file-open path
 * (MentionBadge / FileTypeIcon / useCodeServer / doc-viewer-utils) is stripped —
 * the replay is read-only, so the source file is shown as static text, not a
 * clickable open-in-editor badge. Tailwind classes are replaced by namespaced
 * `replay-*` CSS; lucide icons by the local inline-SVG set.
 */
import type { JSX } from "react";
import type { RemoteFileTransferBlock } from "@shared/loom-message-block";
import { CloudUploadIcon } from "./icons";
import { CopyButton } from "./copy-button";
import { ToolResultPanel } from "./tool-result-panel";

export function RemoteFileTransferBlockView({
  block
}: {
  block: RemoteFileTransferBlock;
}): JSX.Element {
  // Prefer the human-readable remote host name; fall back to the env id.
  const envLabel = block.remoteHostname ?? block.envId;

  return (
    <div className="replay-block replay-remote-file-transfer-block">
      <div className="replay-block-head">
        <CloudUploadIcon className="replay-icon replay-text-cyan" />
        <span className="replay-block-label">
          Remote File Transfer
          {envLabel !== undefined && envLabel.length > 0 && (
            <>
              {" @ "}
              <span className="replay-remote-host">{envLabel}</span>
            </>
          )}
        </span>
        <CopyButton text={block.toPath} className="replay-ml-auto" ariaLabel="复制目标路径" />
      </div>

      <div className="replay-transfer-body">
        <div className="replay-transfer-row">
          <span className="replay-text-muted">file:</span>
          <span className="replay-transfer-file">{block.fileName}</span>
        </div>
        <div className="replay-transfer-row replay-transfer-row-scroll">
          <span className="replay-text-muted">from:</span>
          <span className="replay-mono">{block.fromPath}</span>
        </div>
        <div className="replay-transfer-row replay-transfer-row-scroll">
          <span className="replay-text-muted">to:</span>
          <span className="replay-mono">{block.toPath}</span>
        </div>
      </div>

      {block.result?.output !== undefined && block.result.output.length > 0 && (
        <ToolResultPanel
          title="Result"
          content={block.result.output}
          copyAriaLabel="复制传输结果"
          defaultCollapsed={block.collapsed ?? true}
          status={block.status}
        />
      )}
    </div>
  );
}
